import React, { useState } from "react";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import {
    format,
    startOfWeek,
    endOfWeek,
    isSaturday,
    isSunday,
} from "date-fns";
import { useAuth } from "../../AuthContext";

const PendingTasksSection = ({ BASE_URL }) => {
    const { username } = useAuth();
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [editingTaskId, setEditingTaskId] = useState(null);
    const [editedDescription, setEditedDescription] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const tasksPerPage = 10;

    const toDDMMYYYY = (date) => format(date, "dd-MM-yyyy");

    // ✅ Fetch Pending Tasks for selected date or week
    const handleDateClick = async (date) => {
        setSelectedDate(date);
        setLoading(true);
        setError(null);

        try {
            let endpoint = "";
            if (isSaturday(date) || isSunday(date)) {
                const monday = startOfWeek(date, { weekStartsOn: 1 });
                const sunday = endOfWeek(date, { weekStartsOn: 1 });
                endpoint = `${BASE_URL}/admin/pending-tasks-by-week?start=${toDDMMYYYY(
                    monday
                )}&end=${toDDMMYYYY(sunday)}`;
            } else {
                endpoint = `${BASE_URL}/admin/pending-tasks/${toDDMMYYYY(date)}`;
            }

            const res = await fetch(endpoint);
            if (!res.ok) throw new Error("Failed to fetch pending tasks");
            const data = await res.json();
            setTasks(data);
            setCurrentPage(1);
        } catch (err) {
            console.error(err);
            setError("Failed to load pending tasks. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    // ✅ Inline Edit Handlers
    const handleEditClick = (task) => {
        setEditingTaskId(task.id);
        setEditedDescription("");
    };

    const handleCancelEdit = () => {
        setEditingTaskId(null);
        setEditedDescription("");
    };

    const handleSaveEdit = async (taskId) => {
        if (!editedDescription.trim()) {
            alert("Please enter an updated description.");
            return;
        }
        try {
            const res = await fetch(`${BASE_URL}/admin/tasks/${taskId}/edit`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    description: editedDescription,
                    admin_username: username,
                }),
            });

            if (!res.ok) throw new Error("Failed to update task");

            const updatedTask = await res.json();
            setTasks((prev) =>
                prev.map((task) => (task.id === taskId ? updatedTask : task))
            );
            setEditingTaskId(null);
            setEditedDescription("");
        } catch (err) {
            console.error(err);
            alert("Error updating task.");
        }
    };

    // ✅ Pagination Logic
    const indexOfLastTask = currentPage * tasksPerPage;
    const indexOfFirstTask = indexOfLastTask - tasksPerPage;
    const currentTasks = tasks.slice(indexOfFirstTask, indexOfLastTask);
    const totalPages = Math.ceil(tasks.length / tasksPerPage);

    return (
        <div className="bg-gray-800 p-6 rounded-2xl shadow-2xl text-white animate-fadeIn">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-semibold text-indigo-400">
                    🕓 Pending Tasks
                </h2>
            </div>

            {/* 🌙 Table + Calendar Layout */}
            <div className="flex flex-col lg:flex-row lg:space-x-6 mb-8">
                {/* 📋 Task Table Section */}
                <div className="flex-1 overflow-x-auto mt-4 lg:mt-0">
                    {loading ? (
                        <p className="text-center text-gray-400">Loading pending tasks...</p>
                    ) : error ? (
                        <p className="text-center text-red-400">{error}</p>
                    ) : tasks.length > 0 ? (
                        <>
                            <table className="min-w-full border border-gray-700 table-fixed">
                                <thead>
                                    <tr className="bg-gray-700 text-gray-200">
                                        <th className="py-2 px-4 border border-gray-600 min-w-[100px]">Developer</th>
                                        <th className="py-2 px-4 border border-gray-600 whitespace-nowrap min-w-[100px]">Task Type</th>
                                        <th className="py-2 px-4 border border-gray-600 min-w-[150px]">Project</th>
                                        <th className="py-2 px-4 border border-gray-600 whitespace-nowrap min-w-[100px]">Date</th>
                                        <th className="py-2 px-4 border border-gray-600 whitespace-nowrap min-w-[90px]">Time Slot</th>
                                        <th className="py-2 px-4 border border-gray-600 min-w-[250px] break-words">Description</th>
                                        <th className="py-2 px-4 border border-gray-600 whitespace-nowrap min-w-[90px]">Document</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentTasks.map((task, idx) => (
                                        <tr key={idx} className="text-gray-300 text-sm">
                                            <td className="py-2 px-4 border border-gray-700">{task.developer_name}</td>
                                            <td className="py-2 px-4 border border-gray-700 whitespace-nowrap">{task.task_type}</td>
                                            <td className="py-2 px-4 border border-gray-700">{task.project_name}</td>
                                            <td className="py-2 px-4 border border-gray-700 whitespace-nowrap">{task.date}</td>
                                            <td className="py-2 px-4 border border-gray-700 whitespace-nowrap">{task.time_slot}</td>

                                            {/* 📝 Description & Comments */}
                                            <td className="py-2 px-4 border border-gray-700 break-words min-w-[250px]">
                                                <div>
                                                    <div>{task.description}</div>
                                                    {task.admin_updates?.length > 0 && (
                                                        <div className="mt-1 space-y-1">
                                                            {task.admin_updates.map((update, i) => (
                                                                <div key={i} className="text-indigo-400 text-xs italic">
                                                                    {update.admin_username}: {update.update_text}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>

                                                {editingTaskId !== task.id ? (
                                                    <button
                                                        onClick={() => handleEditClick(task)}
                                                        className="mt-2 bg-blue-600 hover:bg-blue-700 px-3 py-1 rounded text-sm"
                                                    >
                                                        Edit
                                                    </button>
                                                ) : (
                                                    <div className="mt-2 flex space-x-2">
                                                        <input
                                                            type="text"
                                                            value={editedDescription}
                                                            onChange={(e) => setEditedDescription(e.target.value)}
                                                            placeholder="Enter admin update"
                                                            className="px-2 py-1 rounded bg-gray-700 text-white w-full"
                                                        />
                                                        <button
                                                            onClick={() => handleSaveEdit(task.id)}
                                                            className="bg-green-600 px-3 py-1 rounded hover:bg-green-700"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            className="bg-gray-600 px-3 py-1 rounded hover:bg-gray-700"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                )}
                                            </td>

                                            {/* 📄 Document */}
                                            <td className="py-2 px-4 border border-gray-700 text-center">
                                                {task.document_path ? (
                                                    <button
                                                        onClick={() => {
                                                            const docPath = task.document_path.replace(/\\/g, "/");
                                                            const url = docPath.startsWith("/uploads")
                                                                ? `${BASE_URL}${docPath}`
                                                                : `${BASE_URL}/uploads/${docPath.replace(/^uploads\//, "")}`;
                                                            window.open(url, "_blank");
                                                        }}
                                                        className="bg-indigo-600 hover:bg-indigo-700 px-3 py-1 rounded text-sm"
                                                    >
                                                        View
                                                    </button>
                                                ) : (
                                                    <span className="text-gray-400 text-sm italic">No File</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex justify-center items-center mt-4 space-x-2">
                                    <button
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage((p) => p - 1)}
                                        className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
                                    >
                                        Prev
                                    </button>
                                    <span className="text-gray-300">
                                        Page {currentPage} of {totalPages}
                                    </span>
                                    <button
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage((p) => p + 1)}
                                        className="px-3 py-1 bg-gray-700 rounded-md disabled:opacity-50"
                                    >
                                        Next
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <p className="text-center text-gray-400 mt-4">No pending tasks found for this date.</p>
                    )}
                </div>

                {/* 📆 Sticky Calendar Section */}
                <div className="w-full lg:w-[340px] mt-8 lg:mt-0 lg:sticky lg:top-6 self-start">
                    <div className="dark-calendar rounded-xl shadow-lg p-4 bg-gray-900 border border-gray-700">
                        <Calendar
                            onClickDay={handleDateClick}
                            value={selectedDate}
                            calendarType="iso8601"
                            className="react-calendar-custom"
                            next2Label={null}
                            prev2Label={null}
                        />
                    </div>
                </div>
            </div>

            {/* 🌌 Inline Calendar Style Overrides */}
            <style jsx="true">{`
                .react-calendar-custom {
                    width: 100%;
                    max-width: 420px;
                    background: transparent;
                    border: none;
                    color: #e5e7eb;
                    font-family: inherit;
                }

                .react-calendar__navigation {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 8px;
                    color: #e5e7eb;
                }

                .react-calendar__navigation button {
                    background: transparent;
                    color: #e5e7eb;
                    font-weight: 600;
                    border-radius: 6px;
                    padding: 4px 8px;
                    transition: background-color 0.2s ease, color 0.2s ease;
                }

                .react-calendar__navigation button:hover,
                .react-calendar__navigation button:focus {
                    background: #374151;
                    color: #ffffff;
                }

                .react-calendar__month-view__weekdays {
                    text-transform: uppercase;
                    font-size: 0.8rem;
                    color: #9ca3af;
                    border-bottom: 1px solid #374151;
                    padding-bottom: 6px;
                }

                .react-calendar__tile {
                    border-radius: 6px;
                    transition: all 0.2s;
                    color: #d1d5db;
                    transition: background-color 0.2s ease, color 0.2s ease;
                }

                .react-calendar__tile:hover {
                    background: #2d3748;
                    color: #ffffff;
                }

                .react-calendar__tile--active {
                    background: #6366f1;
                    color: #ffffff !important;
                    font-weight: 600;
                }

                .react-calendar__tile--now {
                    background: #2563eb30;
                    border: 1px solid #3b82f6;
                    color: #bfdbfe;
                    font-weight: 600;
                }

                .react-calendar__tile--weekend {
                    color: #f87171;
                }
            `}</style>
        </div>
    );
};

export default PendingTasksSection;
